import { useState, useEffect, useCallback } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { TextInput, Toggle, Select, ColorPicker, RadioGroup, useWpMedia } from '../ui';
import BadgePreview from './BadgePreview';

// ── Reusable card section ─────────────────────────────────
const SectionCard = ({ icon, title, desc, children }) => (
    <div className="cpiw-card">
        <div className="cpiw-card__header">
            <div className="cpiw-card__header-icon"><span style={{ fontSize: 16 }}>{icon}</span></div>
            <div>
                <div className="cpiw-card__title">{title}</div>
                {desc && <div className="cpiw-card__desc">{desc}</div>}
            </div>
        </div>
        <div className="cpiw-card__body">{children}</div>
    </div>
);

// ── Field row ─────────────────────────────────────────────
const Field = ({ label, hint, children, disabled }) => (
    <div className="cpiw-field-row">
        <div className="cpiw-field-label">{label} {disabled === true && <span className="pqd-pro-badge"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 7l4 4 5-7 5 7 4-4-2 13H5L3 7z"/></svg>PRO</span>}{hint && <div className="cpiw-field-hint">{hint}</div>}</div>
        <div className="cpiw-field-control">{children}</div>
    </div>
);

// ── Tabs ──────────────────────────────────────────────────
const TABS = [
    { id: 'default',   label: 'Default Settings' },
    { id: 'design',    label: 'Label Design'      },
    { id: 'product',   label: 'Product Settings'  },
    { id: 'schedule',  label: 'Badge Schedule'    },
];

// ── Shape options for the radio picker ───────────────────
const SHAPES = [
    { value: 'square',           label: 'Square'         },
    { value: 'rectangle',        label: 'Rectangle'      },
    { value: 'rectangle_up',     label: 'Diamond'        },
    { value: 'offers',           label: 'Star Burst'     },
    { value: 'tag',              label: 'Tag'            },
    { value: 'collar',           label: 'Collar'         },
    { value: 'rectangle_round',  label: 'Rounded Rect'   },
    { value: 'rectangle_circle', label: 'Oval'           },
    { value: 'circle',           label: 'Circle'         },
    { value: 'corner_badge',     label: 'Corner'         },
];

const defaultForm = {
    title: '',
    pbfw_show_label: 'on',
    pbfw_show_label_single_pro: 'off',
    pbfw_image_position: 'top_left',
    pbfw_top: '-20', pbfw_left: '-20', pbfw_right: '0', pbfw_bottom: '0',
    badge_define: 'or_text_badge',
    pbfw_discount_text: 'Sale',
    pbfw_font_clr: '#ffffff',
    pbfw_bg_clr: '#6366f1',
    pbfw_ft_size: '14',
    pbfw_font_weight: 'bold',
    pbfw_lbl_shape: 'square',
    pbfw_background: 'or_badge_image',
    image_badge: 'badge.png',
    ocor_bg_img_form: '',
    ocor_bg_img_form_url: '',
    pbfw_pro_condition: 'all_products',
    pbfw_price_condition: 'between',
    pbfw_bet1: '', pbfw_bet2: '', pbfw_price: '',
    pbfw_onsale: 'yes',
    pbfw_cat: [],
    pbfw_tag: [],
    pbfw_combo: [],
    pbfw_start_date: '', pbfw_start_time: '',
    pbfw_end_date:   '', pbfw_end_time:   '',
};

const BadgeForm = ({ editId, toast, onSaved, onCancel }) => {
    const [activeTab, setActiveTab]   = useState('default');
    const [form, setForm]             = useState(defaultForm);
    const [loading, setLoading]       = useState(false);
    const [selectedCats, setSelectedCats] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [products, setProducts]     = useState([]); // selected products
    const [searchQ, setSearchQ]       = useState('');
    const [searchRes, setSearchRes]   = useState([]);
    const [searching, setSearching]   = useState(false);
    const [catSearchQ, setCatSearchQ]     = useState('');
    const [catSearchRes, setCatSearchRes] = useState([]);
    const [catSearching, setCatSearching] = useState(false);
    const [tagSearchQ, setTagSearchQ]     = useState('');
    const [tagSearchRes, setTagSearchRes] = useState([]);
    const [tagSearching, setTagSearching] = useState(false);

    const assetsUrl = window.PBFW_SETTINGS?.assetsUrl || '';

    // wp.media() picker for custom badge image
    const { open: openMediaPicker } = useWpMedia({
        title:      'Select Badge Image',
        buttonText: 'Use this image',
        type:       'image',
        onSelect:   ({ id, url }) => {
            set('ocor_bg_img_form',     String(id));
            set('ocor_bg_img_form_url', url);
        },
    });

    const set = useCallback((key, val) => setForm(prev => ({ ...prev, [key]: val })), []);

    // Load data on mount
    useEffect(() => {
        if (!editId) {
            // Reset to blank form when creating new badge
            setForm(defaultForm);
            setProducts([]);
            setSelectedCats([]);
            setSelectedTags([]);
            return;
        }
        setLoading(true);
        apiFetch({ path: `pbfw/v1/badge/${editId}?nocache=${Date.now()}` })
            .then(async data => {
                setForm({ ...defaultForm, ...data });
                // Load names for previously saved selected products
                if (Array.isArray(data.pbfw_combo) && data.pbfw_combo.length > 0) {
                    const ids = data.pbfw_combo.join(',');
                    apiFetch({ path: `pbfw/v1/products/by-ids?ids=${ids}&nocache=${Date.now()}` })
                        .then(ps => setProducts(ps.map(p => ({ id: p.id, name: p.name }))))
                        .catch(() => {});
                }
                // Load selected categories names (by ids)
                if (Array.isArray(data.pbfw_cat) && data.pbfw_cat.length > 0) {
                    const ids = data.pbfw_cat.join(',');
                    apiFetch({ path: `pbfw/v1/categories/by-ids?ids=${ids}&nocache=${Date.now()}` })
                        .then(cs => setSelectedCats(cs.map(c => ({ id: String(c.id), name: c.name }))))
                        .catch(() => {});
                }
                // Load selected tags names (by ids)
                if (Array.isArray(data.pbfw_tag) && data.pbfw_tag.length > 0) {
                    const ids = data.pbfw_tag.join(',');
                    apiFetch({ path: `pbfw/v1/tags/by-ids?ids=${ids}&nocache=${Date.now()}` })
                        .then(ts => setSelectedTags(ts.map(t => ({ id: String(t.id), name: t.name }))))
                        .catch(() => {});
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [editId]);


    // Product search
    useEffect(() => {
        if (searchQ.length < 3) { setSearchRes([]); return; }
        const timer = setTimeout(() => {
            setSearching(true);
            apiFetch({ path: `pbfw/v1/products/search?q=${encodeURIComponent(searchQ)}&nocache=${Date.now()}` })
                .then(res => { setSearchRes(res); setSearching(false); })
                .catch(() => setSearching(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [searchQ]);

    // Category search (server-side)
    useEffect(() => {
        if (catSearchQ.length < 1) { setCatSearchRes([]); return; }
        const timer = setTimeout(() => {
            setCatSearching(true);
            apiFetch({ path: `pbfw/v1/categories/search?q=${encodeURIComponent(catSearchQ)}&nocache=${Date.now()}` })
                .then(res => {
                    // filter out already selected ids on the client so they don't reappear
                    const filtered = Array.isArray(res) ? res.filter(c => !(Array.isArray(form.pbfw_cat) && form.pbfw_cat.includes(String(c.id)))) : [];
                    setCatSearchRes(filtered);
                    setCatSearching(false);
                })
                .catch(() => setCatSearching(false));
        }, 300);
        return () => clearTimeout(timer);
    }, [catSearchQ, form.pbfw_cat]);

    // Tag search (server-side)
    useEffect(() => {
        if (tagSearchQ.length < 1) { setTagSearchRes([]); return; }
        const timer = setTimeout(() => {
            setTagSearching(true);
            apiFetch({ path: `pbfw/v1/tags/search?q=${encodeURIComponent(tagSearchQ)}&nocache=${Date.now()}` })
                .then(res => {
                    const filtered = Array.isArray(res) ? res.filter(t => !(Array.isArray(form.pbfw_tag) && form.pbfw_tag.includes(String(t.id)))) : [];
                    setTagSearchRes(filtered);
                    setTagSearching(false);
                })
                .catch(() => setTagSearching(false));
        }, 300);
        return () => clearTimeout(timer);
    }, [tagSearchQ, form.pbfw_tag]);

    const addProduct = (p) => {
        if (products.find(x => x.id === p.id)) return;
        const updated = [...products, p];
        setProducts(updated);
        set('pbfw_combo', updated.map(x => x.id));
        setSearchQ(''); setSearchRes([]);
    };

    const removeProduct = (id) => {
        const updated = products.filter(x => x.id !== id);
        setProducts(updated);
        set('pbfw_combo', updated.map(x => x.id));
    };

    const handleSave = () => {
        if (!form.title?.trim()) { toast.error('Badge name is required.'); return; }
        setLoading(true);
        const path   = editId ? `pbfw/v1/badge/${editId}?nocache=${Date.now()}` : `pbfw/v1/badge?nocache=${Date.now()}`;
        const method = editId ? 'PUT' : 'POST';
        apiFetch({ path, method, data: form })
            .then(() => {
                setLoading(false);
                toast.success(editId ? 'Badge updated!' : 'Badge created!');
                onSaved();
            })
            .catch(err => {
                setLoading(false);
                toast.error(err?.message || 'Save failed.');
            });
    };

    const decodeHTML = (html) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
    };

    const showPriceFields    = form.pbfw_pro_condition === 'price';
    const showProductFields  = form.pbfw_pro_condition === 'selected_products';
    const showCategoryFields = form.pbfw_pro_condition === 'category';
    const showTagFields      = form.pbfw_pro_condition === 'tag';
    const showOnsaleFields   = form.pbfw_pro_condition === 'onsale';
    const isBetween          = form.pbfw_price_condition === 'between';
    const isCustomPos        = form.pbfw_image_position === 'custom_position';
    const isTextBadge        = form.badge_define !== 'or_image_badge';
    const isBadgeSelect      = form.pbfw_background !== 'or_badge_image_upload';

    const BADGE_IMAGES = ['badge.png', 'badge1.png', 'badge2.png', 'badge3.png'];

    return (
        <div className="pbfw-form-page">
            {loading && (
                <div className="cpiw-overlay-loader">
                    <div className="cpiw-spinner-wrap">
                        <div className="cpiw-spinner" />
                        <span className="cpiw-spinner-label">{editId ? 'Loading…' : 'Saving…'}</span>
                    </div>
                </div>
            )}

            {/* Edit banner */}
            {editId && (
                <div className="cpiw-edit-banner">
                    <span>✎ Editing badge <strong>#{editId}</strong></span>
                    <button className="cpiw-btn-secondary cpiw-btn-sm" onClick={onCancel}>← Back</button>
                </div>
            )}

            <div className="pbfw-form-layout">
                {/* ── Left: form tabs ─── */}
                <div className="pbfw-form-main">

                    {/* Badge name */}
                    <SectionCard icon="🏷️" title="Badge Name" desc="Give this badge a name for your reference.">
                        <div className="cpiw-field-row">
                            <div className="cpiw-field-label">Badge Name <span style={{ color: 'var(--cpiw-danger)', marginLeft: 2 }}>*</span></div>
                            <div className="cpiw-field-control">
                                <TextInput value={form.title} onChange={v => set('title', v)} placeholder="e.g. Summer Sale Badge" />
                            </div>
                        </div>
                    </SectionCard>

                    {/* Tab nav */}
                    <div className="cpiw-tab-nav" style={{ marginBottom: 20 }}>
                        {TABS.map(t => (
                            <button
                                key={t.id}
                                className={`cpiw-tab-btn${activeTab === t.id ? ' cpiw-tab-btn--active' : ''}`}
                                onClick={() => setActiveTab(t.id)}
                            >{t.label}</button>
                        ))}
                    </div>

                    {/* ── TAB: Default Settings ── */}
                    {activeTab === 'default' && (
                        <SectionCard icon="⚙️" title="Default Settings" desc="Control badge visibility and position.">
                            <Field label="Show Label">
                                <Toggle checked={form.pbfw_show_label === 'on'} onChange={v => set('pbfw_show_label', v ? 'on' : 'off')} label />
                            </Field>
                            <Field label="Show on Single Product Page" disabled={true}>
                                <div style={{ pointerEvents: 'none', opacity: 0.5 }}>
                                    <Toggle checked={form.pbfw_show_label_single_pro === 'on'} onChange={v => set('pbfw_show_label_single_pro', v ? 'on' : 'off')} label />
                                </div>
                                <span style={{ fontSize: 12, color: 'var(--cpiw-text-3)', marginTop: 4, display: 'block' }}>
                                    This Option Available in{' '}
                                    <a
                                        href="https://www.plugin999.com/plugin/product-badges-for-woocommerce/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'var(--cpiw-brand)', fontWeight: 600, textDecoration: 'underline', }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        Pro Version
                                    </a>
                                </span>
                            </Field>
                            <Field label="Badge Position">
                                <Select
                                    value={form.pbfw_image_position}
                                    onChange={v => set('pbfw_image_position', v)}
                                    options={[
                                        { value: 'top_left',        label: 'Top Left'        },
                                        { value: 'top_right',       label: 'Top Right'       },
                                        { value: 'bottom_left',     label: 'Bottom Left'     },
                                        { value: 'bottom_right',    label: 'Bottom Right'    },
                                        { value: 'custom_position', label: 'Custom Position' },
                                    ]}
                                />
                            </Field>
                            {isCustomPos && (
                                <Field label="Custom Offset (px)" hint="Set exact pixel offsets from each edge.">
                                    <div className="pbfw-pos-grid">
                                        {['top','left','right','bottom'].map(dir => (
                                            <label key={dir} className="pbfw-pos-item">
                                                <span>{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
                                                <TextInput type="number" value={form[`pbfw_${dir}`]} onChange={v => set(`pbfw_${dir}`, v)} />
                                            </label>
                                        ))}
                                    </div>
                                </Field>
                            )}
                        </SectionCard>
                    )}

                    {/* ── TAB: Label Design ── */}
                    {activeTab === 'design' && (
                        <>
                            {/* Badge type picker */}
                            <SectionCard icon="🎨" title="Badge Type" desc="Choose between a text badge or an image badge.">
                                <div className="pbfw-badge-type-row">
                                    {[
                                        { value: 'or_text_badge',  label: '🔤 Text Badge'  },
                                        { value: 'or_image_badge', label: '🖼 Image Badge' },
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            className={`pbfw-type-btn${form.badge_define === opt.value ? ' pbfw-type-btn--active' : ''}`}
                                            onClick={() => set('badge_define', opt.value)}
                                        >{opt.label}</button>
                                    ))}
                                </div>
                            </SectionCard>

                            {/* Text badge settings */}
                            {isTextBadge && (
                                <SectionCard icon="🔤" title="Text Badge Settings" desc="Configure the text, colours, and shape of your badge.">
                                    <Field label="Badge Text" hint="Use <br> for line breaks.">
                                        <TextInput value={form.pbfw_discount_text} onChange={v => set('pbfw_discount_text', v)} placeholder="Sale" />
                                    </Field>
                                    <Field label="Font Color">
                                        <ColorPicker value={form.pbfw_font_clr} onChange={v => set('pbfw_font_clr', v)} />
                                    </Field>
                                    <Field label="Background Color">
                                        <ColorPicker value={form.pbfw_bg_clr} onChange={v => set('pbfw_bg_clr', v)} />
                                    </Field>
                                    <Field label="Font Size (px)">
                                        <TextInput type="number" value={form.pbfw_ft_size} onChange={v => set('pbfw_ft_size', v)} />
                                    </Field>
                                    <Field label="Font Weight">
                                        <Select
                                            value={form.pbfw_font_weight}
                                            onChange={v => set('pbfw_font_weight', v)}
                                            options={[
                                                { value: '',       label: 'Default' },
                                                { value: 'normal', label: 'Normal'    },
                                                { value: '600',    label: 'Semi Bold' },
                                                { value: 'bold',   label: 'Bold'      },
                                            ]}
                                        />
                                    </Field>
                                    <Field label="Badge Shape">
                                        <div className="pbfw-shape-grid">
                                            {SHAPES.map(s => (
                                                <button
                                                    key={s.value}
                                                    title={s.label}
                                                    className={`pbfw-shape-btn${form.pbfw_lbl_shape === s.value ? ' pbfw-shape-btn--active' : ''}`}
                                                    onClick={() => set('pbfw_lbl_shape', s.value)}
                                                >
                                                    <div className={`pbfw-shape-thumb pbfw-shape-thumb--${s.value}`}
                                                         style={{ background: form.pbfw_bg_clr, color: form.pbfw_font_clr }} />
                                                    <span>{s.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </Field>
                                </SectionCard>
                            )}

                            {/* Image badge settings */}
                            {!isTextBadge && (
                                <SectionCard icon="🖼" title="Image Badge Settings" desc="Choose a preset badge image or upload your own.">
                                    <Field label="Image Source">
                                        <div className="pbfw-badge-type-row">
                                            {[
                                                { value: 'or_badge_image',        label: '📦 Preset Badges'  },
                                                { value: 'or_badge_image_upload', label: '⬆ Upload Custom'  },
                                            ].map(opt => (
                                                <button
                                                    key={opt.value}
                                                    className={`pbfw-type-btn${form.pbfw_background === opt.value ? ' pbfw-type-btn--active' : ''}`}
                                                    onClick={() => set('pbfw_background', opt.value)}
                                                >{opt.label}</button>
                                            ))}
                                        </div>
                                    </Field>
                                    {isBadgeSelect && (
                                        <Field label="Choose Badge Image">
                                            <div className="pbfw-badge-img-row">
                                                {BADGE_IMAGES.map(img => (
                                                    <button
                                                        key={img}
                                                        className={`pbfw-badge-img-btn${form.image_badge === img ? ' pbfw-badge-img-btn--active' : ''}`}
                                                        onClick={() => set('image_badge', img)}
                                                    >
                                                        <img src={`${assetsUrl}/img/${img}`} alt={img} />
                                                    </button>
                                                ))}
                                            </div>
                                        </Field>
                                    )}
                                    {!isBadgeSelect && (
                                        <Field label="Custom Badge Image" hint="Opens the WordPress Media Library — upload or choose an existing image.">
                                            <div className="cpiw-upload-actions">
                                                <button
                                                    type="button"
                                                    className="cpiw-file-btn"
                                                    onClick={openMediaPicker}
                                                >
                                                    🖼 Choose from Media Library
                                                </button>
                                                {form.ocor_bg_img_form_url ? (
                                                    <div className="pbfw-uploaded-preview">
                                                        <img src={form.ocor_bg_img_form_url} alt="Custom badge" />
                                                        <button
                                                            className="cpiw-btn-danger cpiw-btn--sm"
                                                            onClick={() => {
                                                                set('ocor_bg_img_form',     '');
                                                                set('ocor_bg_img_form_url', '');
                                                            }}
                                                        >✕ Remove</button>
                                                    </div>
                                                ) : (
                                                    <span className="cpiw-file-none">No image selected</span>
                                                )}
                                            </div>
                                        </Field>
                                    )}
                                </SectionCard>
                            )}
                        </>
                    )}

                    {/* ── TAB: Product Settings ── */}
                    {activeTab === 'product' && (
                        <>
                            <SectionCard icon="📦" title="Product Condition" desc="Define which products this badge applies to.">
                                <Field label="Show Badge On">
                                    <div className="pbfw-radio-grid">
                                        {[
                                        { value: 'all_products', label: 'All Products' },
                                        { value: 'selected_products', label: 'Selected Products' },
                                        { value: 'price', label: 'By Price', disabled: true },
                                        { value: 'category', label: 'By Category', disabled: true },
                                        { value: 'tag', label: 'By Tag', disabled: true },
                                        { value: 'outofstock', label: 'Out of Stock', disabled: true },
                                        { value: 'onsale', label: 'On Sale' },
                                        ].map(opt => {
                                        const checked = form.pbfw_pro_condition === opt.value;

                                        return (
                                            <label
                                            key={opt.value}
                                            className={`pbfw-card 
                                                ${checked ? 'is-active' : ''} 
                                                ${opt.disabled ? 'is-disabled' : ''}`}
                                            >
                                            {/* Hidden radio (keeps logic intact) */}
                                            <input
                                                type="radio"
                                                name="pbfw_pro_condition"
                                                value={opt.value}
                                                checked={checked}
                                                onChange={() => {
                                                if (!opt.disabled) {
                                                    set('pbfw_pro_condition', opt.value);
                                                }
                                                }}
                                                disabled={opt.disabled}
                                            />

                                            {/* Top row */}
                                            <div className="pbfw-card-header">
                                                <span>{opt.label}</span>

                                                {opt.disabled && <span className="pqd-pro-badge"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 7l4 4 5-7 5 7 4-4-2 13H5L3 7z"/></svg>PRO</span>}
                                            </div>

                                            {/* Bottom row */}
                                            {opt.disabled && (
                                                <div className="pbfw-card-desc">
                                                    {opt.disabled && (
                                                    <>
                                                        Available in{' '}
                                                        <a
                                                        href="https://www.plugin999.com/plugin/product-badges-for-woocommerce/"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        >
                                                        Pro Version
                                                        </a>
                                                    </>
                                                    )}
                                                </div>
                                            )}
                                            </label>
                                        );
                                        })}
                                    </div>
                                </Field>
                            </SectionCard>

                            {showPriceFields && (
                                <SectionCard icon="💲" title="Price Rule" desc="Apply badge based on product price.">
                                    <Field label="Price Condition">
                                        <Select
                                            value={form.pbfw_price_condition}
                                            onChange={v => set('pbfw_price_condition', v)}
                                            options={[
                                                { value: 'between',     label: 'Between'       },
                                                { value: 'lessthan',    label: 'Less Than'     },
                                                { value: 'greaterthan', label: 'Greater Than'  },
                                            ]}
                                        />
                                    </Field>
                                    {isBetween ? (
                                        <>
                                            <Field label="Minimum Price">
                                                <TextInput type="number" value={form.pbfw_bet1} onChange={v => set('pbfw_bet1', v)} placeholder="0" />
                                            </Field>
                                            <Field label="Maximum Price">
                                                <TextInput type="number" value={form.pbfw_bet2} onChange={v => set('pbfw_bet2', v)} placeholder="999" />
                                            </Field>
                                        </>
                                    ) : (
                                        <Field label="Conditional Price">
                                            <TextInput type="number" value={form.pbfw_price} onChange={v => set('pbfw_price', v)} placeholder="0" />
                                        </Field>
                                    )}
                                </SectionCard>
                            )}

                            {showProductFields && (
                                <SectionCard icon="🛍" title="Select Products" desc="Type to search and select specific products.">
                                    <Field label="Search Products">
                                        <div className="pbfw-product-search">
                                            <TextInput value={searchQ} onChange={setSearchQ} placeholder="Type at least 3 characters…" />
                                            {searching && <span className="pbfw-search-spinner">⏳</span>}
                                            {searchRes.length > 0 && (
                                                <div className="pbfw-search-results">
                                                    {searchRes.map(p => (
                                                        <button key={p.id} className="pbfw-search-result-item" onClick={() => addProduct(p)}>
                                                            <strong>{decodeHTML(p.name)}</strong>
                                                            <span dangerouslySetInnerHTML={{ __html: p.price }} />
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Field>
                                    {products.length > 0 && (
                                        <Field label="Selected Products">
                                            <div className="pbfw-selected-products">
                                                {products.map(p => (
                                                    <span key={p.id} className="pbfw-product-tag">
                                                        {decodeHTML(p.name)}
                                                        <button onClick={() => removeProduct(p.id)}>✕</button>
                                                    </span>
                                                ))}
                                            </div>
                                        </Field>
                                    )}
                                </SectionCard>
                            )}

                            {showCategoryFields && (
                                <SectionCard icon="📁" title="Category Rule" desc="Apply badge to products in specific categories.">
                                    <Field label="Categories">
                                        <div className="pbfw-product-search">
                                            <TextInput value={catSearchQ} onChange={setCatSearchQ} placeholder="Type to search categories…" />
                                            {catSearching && <span className="pbfw-search-spinner">⏳</span>}
                                            {catSearchRes.length > 0 && (
                                                <div className="pbfw-search-results">
                                                    {catSearchRes.map(cat => (
                                                        <button key={cat.id} className="pbfw-search-result-item" onClick={() => {
                                                            const cur = Array.isArray(form.pbfw_cat) ? form.pbfw_cat.map(String) : [];
                                                            if (cur.includes(String(cat.id))) return;
                                                            const updated = [...cur, String(cat.id)];
                                                            set('pbfw_cat', updated);
                                                            setSelectedCats(prev => [...prev, { id: String(cat.id), name: cat.name }]);
                                                            setCatSearchQ('');
                                                            setCatSearchRes([]);
                                                        }}>
                                                            <strong>{cat.name}</strong>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {Array.isArray(form.pbfw_cat) && form.pbfw_cat.length > 0 && (
                                            <div className="pbfw-selected-products">
                                                {form.pbfw_cat.map(id => {
                                                    const c = selectedCats.find(x => String(x.id) === String(id));
                                                    return (
                                                        <span key={id} className="pbfw-product-tag">
                                                            {c ? c.name : `#${id}`}
                                                            <button onClick={() => {
                                                                const cur = Array.isArray(form.pbfw_cat) ? form.pbfw_cat.map(String) : [];
                                                                set('pbfw_cat', cur.filter(x => x !== String(id)));
                                                                setSelectedCats(prev => prev.filter(x => String(x.id) !== String(id)));
                                                            }}>✕</button>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </Field>
                                </SectionCard>
                            )}

                            {showTagFields && (
                                <SectionCard icon="🔖" title="Tag Rule" desc="Apply badge to products with specific tags.">
                                    <Field label="Tags">
                                        <div className="pbfw-product-search">
                                            <TextInput value={tagSearchQ} onChange={setTagSearchQ} placeholder="Type to search tags…" />
                                            {tagSearching && <span className="pbfw-search-spinner">⏳</span>}
                                            {tagSearchRes.length > 0 && (
                                                <div className="pbfw-search-results">
                                                            {tagSearchRes.map(tag => (
                                                                <button key={tag.id} className="pbfw-search-result-item" onClick={() => {
                                                                    const cur = Array.isArray(form.pbfw_tag) ? form.pbfw_tag.map(String) : [];
                                                                    if (cur.includes(String(tag.id))) return;
                                                                    const updated = [...cur, String(tag.id)];
                                                                    set('pbfw_tag', updated);
                                                                    setSelectedTags(prev => [...prev, { id: String(tag.id), name: tag.name }]);
                                                                    setTagSearchQ('');
                                                                    setTagSearchRes([]);
                                                                }}>
                                                                    <strong>{tag.name}</strong>
                                                                </button>
                                                            ))}
                                                </div>
                                            )}
                                        </div>
                                        {Array.isArray(form.pbfw_tag) && form.pbfw_tag.length > 0 && (
                                            <div className="pbfw-selected-products">
                                                {form.pbfw_tag.map(id => {
                                                    const t = selectedTags.find(x => String(x.id) === String(id));
                                                    return (
                                                        <span key={id} className="pbfw-product-tag">
                                                            {t ? t.name : `#${id}`}
                                                            <button onClick={() => {
                                                                const cur = Array.isArray(form.pbfw_tag) ? form.pbfw_tag.map(String) : [];
                                                                set('pbfw_tag', cur.filter(x => x !== String(id)));
                                                                setSelectedTags(prev => prev.filter(x => String(x.id) !== String(id)));
                                                            }}>✕</button>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </Field>
                                </SectionCard>
                            )}

                            {showOnsaleFields && (
                                <SectionCard icon="🏷" title="On Sale" desc="Show badge when product is on sale.">
                                    <Field label="Is On Sale">
                                        <Select
                                            value={form.pbfw_onsale}
                                            onChange={v => set('pbfw_onsale', v)}
                                            options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
                                        />
                                    </Field>
                                </SectionCard>
                            )}
                        </>
                    )}

                    {/* ── TAB: Schedule ── */}
                    {activeTab === 'schedule' && (
                        <SectionCard icon="🗓" title="Badge Schedule" desc="Set a start and end date/time for this badge. Leave blank for always active.">
                            <Field label="From Date / Time">
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <input className="cpiw-input" type="date" value={form.pbfw_start_date} onChange={e => set('pbfw_start_date', e.target.value)} style={{ maxWidth: 180 }} />
                                    <input className="cpiw-input" type="time" value={form.pbfw_start_time} onChange={e => set('pbfw_start_time', e.target.value)} style={{ maxWidth: 140 }} />
                                </div>
                            </Field>
                            <Field label="To Date / Time">
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <input className="cpiw-input" type="date" value={form.pbfw_end_date} onChange={e => set('pbfw_end_date', e.target.value)} style={{ maxWidth: 180 }} />
                                    <input className="cpiw-input" type="time" value={form.pbfw_end_time} onChange={e => set('pbfw_end_time', e.target.value)} style={{ maxWidth: 140 }} />
                                </div>
                            </Field>
                            <p style={{ fontSize: 12.5, color: 'var(--cpiw-text-3)', marginTop: 8 }}>
                                Times are based on your server timezone settings.
                            </p>
                        </SectionCard>
                    )}

                    {/* Save bar */}
                    <div className="cpiw-save-bar">
                        <p className="cpiw-save-bar__hint">
                            <strong>⚠ Unsaved changes</strong> — Click Save to apply your settings.
                        </p>
                        <div className="cpiw-save-bar__actions">
                            <button className="cpiw-btn-secondary" onClick={onCancel}>Cancel</button>
                            <button className="cpiw-btn" onClick={handleSave}>
                                {editId ? '✓ Update Badge' : '+ Create Badge'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Right: live preview ─── */}
                <div className="pbfw-form-sidebar">
                    <BadgePreview form={form} assetsUrl={assetsUrl} />
                </div>
            </div>
        </div>
    );
};

export default BadgeForm;
